import type { User } from "@/api/types";
import { apiUrl } from "@/config";
import { cn } from "@/lib/utils";

/** Deterministic, pleasant hue (0–360) from the username for the fallback gradient. */
function avatarHue(seed: string): number {
  let h = 0;
  for (let i = 0; i < seed.length; i++) {
    h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  }
  return h % 360;
}

/**
 * Renders a user's avatar from the backend avatar endpoint, falling back to the
 * user's initial over a soft per-user gradient. Uses the /file/users/{username}/avatar
 * route (public on open instances; cookie/bearer-authenticated on private ones).
 */
export function UserAvatar({ user, size = "md", className }: { user: User; size?: "sm" | "md" | "lg"; className?: string }) {
  const px = size === "sm" ? "h-8 w-8 text-xs" : size === "lg" ? "h-16 w-16 text-2xl" : "h-10 w-10 text-sm";
  const seed = user.displayName || user.username || "?";
  const initial = seed.charAt(0).toUpperCase();
  const src = user.avatarUrl || apiUrl(`/file/users/${encodeURIComponent(user.username)}/avatar`);
  const hue = avatarHue(user.username || seed);
  return (
    <span
      className={cn("relative inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full font-semibold text-white", px, className)}
      style={{
        backgroundImage: `linear-gradient(135deg, hsl(${hue} 62% 58%), hsl(${(hue + 28) % 360} 60% 44%))`,
      }}
    >
      <span aria-hidden="true" className="drop-shadow-[0_1px_1px_rgb(0_0_0/0.25)]">{initial}</span>
      <img
        src={src}
        alt=""
        loading="lazy"
        className="absolute inset-0 h-full w-full object-cover"
        onError={(e) => {
          // Hide broken avatar images so the initial shows through.
          (e.target as HTMLImageElement).style.display = "none";
        }}
      />
    </span>
  );
}
